import React, { useState, useEffect } from 'react';
import { User, Mail, Camera, MapPin, Shield, Smartphone, Key, X, Edit2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { ImageCropUploader } from '../common/ImageCropUploader';
import { EmailChangeModal } from '../common/EmailChangeModal';
import { GoogleAddressAutocomplete, AddressDetails } from '../common/GoogleAddressAutocomplete';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { CodeInput } from '../common/CodeInput';
import { PhoneInput } from '../common/PhoneInput';
import { EmailPreferences } from '../EmailPreferences';

interface AccountData {
  _id: string;
  companyName: string;
  address?: {
    formatted?: string;
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
}

export const AdminProfile: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();
  const [account, setAccount] = useState<AccountData | null>(null);
  const [profileImage, setProfileImage] = useState<string | null>((user as any)?.profileImage || null);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  // Inline editing states
  const [editingFirstName, setEditingFirstName] = useState(false);
  const [editingLastName, setEditingLastName] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);
  const [editingPhone, setEditingPhone] = useState(false);
  
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phoneNumber, setPhoneNumber] = useState((user as any)?.smsPhoneNumber || '');
  const [addressFormatted, setAddressFormatted] = useState('');
  const [addressDetails, setAddressDetails] = useState<AddressDetails | null>(null);
  
  const [savingFirstName, setSavingFirstName] = useState(false);
  const [savingLastName, setSavingLastName] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingPhone, setSavingPhone] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  
  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  
  const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);
  const [pendingNewEmail, setPendingNewEmail] = useState('');
  
  // 2FA states
  const [twoFAStatus, setTwoFAStatus] = useState<any>(null);
  const [loading2FA, setLoading2FA] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [manualEntryKey, setManualEntryKey] = useState<string | null>(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [smsPhoneNumber, setSmsPhoneNumber] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [smsCodeSent, setSmsCodeSent] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [showDisable2FADialog, setShowDisable2FADialog] = useState(false);
  const [show2FASetupModal, setShow2FASetupModal] = useState(false);
  const [selected2FAMethods, setSelected2FAMethods] = useState<{ authenticator: boolean; sms: boolean }>({ authenticator: false, sms: false });
  const [authenticatorVerified, setAuthenticatorVerified] = useState(false);
  const [smsVerified, setSmsVerified] = useState(false);
  const [setupStep, setSetupStep] = useState<'select' | 'authenticator' | 'sms'>('select');
  const [setupOrder, setSetupOrder] = useState<Array<'authenticator' | 'sms'>>([]);
  
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Helper function to convert country name to ISO code
  const getCountryCode = (countryName: string | undefined): string | undefined => {
    if (!countryName) return undefined;
    
    const countryMap: { [key: string]: string } = {
      'new zealand': 'nz',
      'australia': 'au',
      'united states': 'us',
      'united states of america': 'us',
      'usa': 'us',
      'united kingdom': 'gb',
      'uk': 'gb',
      'canada': 'ca',
      'south africa': 'za',
      'ireland': 'ie',
      'singapore': 'sg',
      'malaysia': 'my',
      'philippines': 'ph',
      'indonesia': 'id',
      'thailand': 'th',
      'vietnam': 'vn',
      'japan': 'jp',
      'south korea': 'kr',
      'china': 'cn',
      'india': 'in',
      'france': 'fr',
      'germany': 'de',
      'italy': 'it',
      'spain': 'es',
      'netherlands': 'nl',
      'belgium': 'be',
      'switzerland': 'ch',
      'austria': 'at',
      'sweden': 'se',
      'norway': 'no',
      'denmark': 'dk',
      'finland': 'fi',
      'poland': 'pl',
      'portugal': 'pt',
      'brazil': 'br',
      'mexico': 'mx',
      'argentina': 'ar',
      'chile': 'cl',
      'colombia': 'co',
      'peru': 'pe',
      'uae': 'ae',
      'united arab emirates': 'ae',
      'saudi arabia': 'sa',
      'israel': 'il',
      'turkey': 'tr',
      'egypt': 'eg',
      'nigeria': 'ng',
      'kenya': 'ke'
    };
    
    const normalized = countryName.toLowerCase().trim();
    return countryMap[normalized];
  };

  // Fetch account data to get country for phone input default
  useEffect(() => {
    const fetchAccount = async () => {
      if (!user?.accountId) return;

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${apiUrl}/api/accounts/${user.accountId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setAccount(data.account);
        }
      } catch (error) {
        console.error('Failed to fetch account:', error);
      }
    };

    fetchAccount();
  }, [user?.accountId, apiUrl]);

  const showNotification = (message: string) => {
    setSaveMessage(message);
    setShowSaveNotification(true);
    setTimeout(() => setShowSaveNotification(false), 2000);
  };

  // Fetch 2FA status
  const fetch2FAStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/auth/2fa/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        console.log('🔐 [2FA Status] Received:', data);
        setTwoFAStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch 2FA status:', error);
    }
  };

  // Update local state when user changes
  useEffect(() => {
    console.log('🟡 [PROFILE] User data changed in useEffect');
    // Log user object without profileImage to avoid cluttering console
    const userWithoutImage = user ? { ...user, profileImage: '[REDACTED]' } : null;
    console.log('🟡 [PROFILE] Full user object:', JSON.stringify(userWithoutImage, null, 2));
    setProfileImage((user as any)?.profileImage || null);
    setFirstName(user?.firstName || '');
    setLastName(user?.lastName || '');
    setEmail(user?.email || '');
    const userAddress = (user as any)?.address;
    console.log('🟡 [PROFILE] Extracted userAddress:', JSON.stringify(userAddress, null, 2));
    if (userAddress) {
      const addressDetailsObj = {
        formatted: userAddress.formatted || [userAddress.street, userAddress.city, userAddress.state, userAddress.postalCode, userAddress.country].filter(Boolean).join(', '),
        streetNumber: userAddress.streetNumber,
        streetName: userAddress.streetName,
        street: userAddress.street,
        city: userAddress.city,
        state: userAddress.state,
        postalCode: userAddress.postalCode,
        country: userAddress.country,
        lat: userAddress.lat,
        lng: userAddress.lng
      };
      console.log('🟡 [PROFILE] Setting addressDetails to:', JSON.stringify(addressDetailsObj, null, 2));
      setAddressDetails(addressDetailsObj);
      const formatted = userAddress.formatted || [userAddress.street, userAddress.city, userAddress.state, userAddress.postalCode, userAddress.country].filter(Boolean).join(', ');
      console.log('🟡 [PROFILE] Setting addressFormatted to:', formatted);
      setAddressFormatted(formatted);
    } else {
      console.log('🟡 [PROFILE] No address found, clearing address details');
      setAddressDetails(null);
      setAddressFormatted('');
    }
    
    // Fetch 2FA status when user loads
    if (user) {
      fetch2FAStatus();
    }
    
    // Update phone number when user changes
    if (user) {
      setPhoneNumber((user as any)?.smsPhoneNumber || '');
    }
  }, [user]);

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert(t('admin.pleaseSelectImageFile'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert(t('admin.imageMustBeLessThan5MB'));
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setSelectedFile(event.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleImageCropped = async (croppedBlob: Blob, previewUrl: string) => {
    setUploading(true);
    setSelectedFile(null);

    try {
      // Set preview immediately
      setProfileImage(previewUrl);

      // Upload to server
      const formData = new FormData();
      formData.append('image', croppedBlob, 'profile.jpg');
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/users/profile-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to upload image');
      }

      // Update with server-returned image
      setProfileImage(data.data.profileImage);
      
      // Refresh user context to persist the image
      if (refreshUser) {
        await refreshUser();
      }
      
      showNotification(t('admin.profilePictureSaved'));
    } catch (error) {
      console.error('Failed to upload image:', error);
      // Revert to previous image on error
      setProfileImage((user as any)?.profileImage || null);
    } finally {
      setUploading(false);
    }
  };

  const handleSaveFirstName = async () => {
    if (!user?._id || !firstName.trim() || firstName === user?.firstName) {
      setEditingFirstName(false);
      return;
    }
    
    setSavingFirstName(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/users/${user._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ firstName })
      });

      const data = await response.json();
      if (data.success) {
        if (refreshUser) await refreshUser();
        setEditingFirstName(false);
        showNotification(t('admin.firstNameSaved'));
      } else {
        throw new Error(data.message || 'Failed to update first name');
      }
    } catch (error) {
      console.error('Failed to update first name:', error);
      setFirstName(user?.firstName || '');
    } finally {
      setSavingFirstName(false);
    }
  };

  const handleSaveLastName = async () => {
    if (!user?._id || !lastName.trim() || lastName === user?.lastName) {
      setEditingLastName(false);
      return;
    }
    
    setSavingLastName(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/users/${user._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ lastName })
      });

      const data = await response.json();
      if (data.success) {
        if (refreshUser) await refreshUser();
        setEditingLastName(false);
        showNotification(t('admin.lastNameSaved'));
      } else {
        throw new Error(data.message || 'Failed to update last name');
      }
    } catch (error) {
      console.error('Failed to update last name:', error);
      setLastName(user?.lastName || '');
    } finally {
      setSavingLastName(false);
    }
  };

  const handleSaveEmail = async () => {
    if (!user?._id || !email.trim() || email === user?.email) {
      setEditingEmail(false);
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setSaveMessage(t('admin.pleaseEnterValidEmail'));
      setShowSaveNotification(true);
      setTimeout(() => setShowSaveNotification(false), 3000);
      setEmail(user?.email || '');
      setEditingEmail(false);
      return;
    }
    
    setSavingEmail(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/auth/request-email-change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ newEmail: email })
      });

      const data = await response.json();
      if (data.success) {
        setPendingNewEmail(email);
        setEditingEmail(false);
        setShowEmailChangeModal(true);
      } else {
        throw new Error(data.message || 'Failed to request email change');
      }
    } catch (error: any) {
      console.error('Failed to request email change:', error);
      const errorMessage = error?.message || 'Failed to request email change';
      setSaveMessage(errorMessage);
      setShowSaveNotification(true);
      setTimeout(() => setShowSaveNotification(false), 3000);
      setEmail(user?.email || '');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleEmailChangeSuccess = async () => {
    if (refreshUser) {
      await refreshUser();
    }
    showNotification(t('admin.emailChangedSuccessfully'));
  };

  const handleAddressSelect = (details: AddressDetails) => {
    console.log('🔵 [PROFILE] Address selected from autocomplete:', details);
    setAddressDetails(details);
    setAddressFormatted(details.formatted);
    console.log('🔵 [PROFILE] Address state updated');
  };

  // 2FA handlers
  const handleSetupAuthenticator = async () => {
    setLoading2FA(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/auth/2fa/setup/authenticator`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setQrCode(data.qrCode);
        setManualEntryKey(data.manualEntryKey);
      } else {
        showNotification(data.message || t('admin.twoFactor.setupFailed'));
      }
    } catch (error: any) {
      showNotification(error.message || t('admin.twoFactor.setupFailed'));
    } finally {
      setLoading2FA(false);
    }
  };

  const handleOpen2FASetupModal = () => {
    setShow2FASetupModal(true);
    setSelected2FAMethods({ authenticator: false, sms: false });
    setAuthenticatorVerified(false);
    setSmsVerified(false);
    setSetupStep('select');
    setSetupOrder([]);
    setQrCode(null);
    setManualEntryKey(null);
    setTwoFACode('');
    // Pre-populate phone number from user's profile if available
    setSmsPhoneNumber((user as any)?.smsPhoneNumber || '');
    setSmsCode('');
    setSmsCodeSent(false);
  };

  const handleClose2FASetupModal = () => {
    setShow2FASetupModal(false);
    setSelected2FAMethods({ authenticator: false, sms: false });
    setAuthenticatorVerified(false);
    setSmsVerified(false);
    setSetupStep('select');
    setSetupOrder([]);
    setQrCode(null);
    setManualEntryKey(null);
    setTwoFACode('');
    setSmsPhoneNumber('');
    setSmsCode('');
    setSmsCodeSent(false);
  };

  const handleToggleMethod = (method: 'authenticator' | 'sms') => {
    setSelected2FAMethods(prev => ({
      ...prev,
      [method]: !prev[method]
    }));
  };

  const handleNextFromSelection = () => {
    const methods: Array<'authenticator' | 'sms'> = [];
    if (selected2FAMethods.authenticator) methods.push('authenticator');
    if (selected2FAMethods.sms) methods.push('sms');
    
    if (methods.length === 0) {
      showNotification('Please select at least one 2FA method');
      return;
    }
    
    setSetupOrder(methods);
    setSetupStep(methods[0]);
    
    // Start setup for first method
    if (methods[0] === 'authenticator') {
      handleSetupAuthenticator();
    }
  };

  const handleVerifyAuthenticator = async () => {
    if (!twoFACode || twoFACode.length !== 6) {
      showNotification(t('admin.twoFactor.enterCode'));
      return;
    }
    setLoading2FA(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/auth/2fa/verify/authenticator`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code: twoFACode })
      });
      const data = await response.json();
      if (data.success) {
        setBackupCodes(data.backupCodes || []);
        setAuthenticatorVerified(true);
        setShowBackupCodes(true);
        setQrCode(null);
        setManualEntryKey(null);
        setTwoFACode('');
        await fetch2FAStatus();
        showNotification(t('admin.twoFactor.authenticatorVerified'));
        
        // Don't auto-advance, let user click "Next" button
      } else {
        showNotification(data.message || t('admin.twoFactor.invalidCode'));
      }
    } catch (error: any) {
      showNotification(error.message || t('admin.twoFactor.verifyFailed'));
    } finally {
      setLoading2FA(false);
    }
  };

  const handleSetupSMS = async () => {
    if (!smsPhoneNumber) {
      showNotification(t('admin.twoFactor.enterPhoneNumber'));
      return;
    }
    setLoading2FA(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/auth/2fa/setup/sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ phoneNumber: smsPhoneNumber })
      });
      const data = await response.json();
      if (data.success) {
        setSmsCodeSent(true); // Mark that code has been sent
        showNotification(t('admin.twoFactor.smsCodeSent'));
      } else {
        // Show the backend error message which will explain if SMS is not configured
        showNotification(data.message || t('admin.twoFactor.setupFailed'));
      }
    } catch (error: any) {
      showNotification(t('admin.twoFactor.setupFailed'));
    } finally {
      setLoading2FA(false);
    }
  };

  const handleVerifySMS = async () => {
    if (!smsCode || smsCode.length !== 6) {
      showNotification(t('admin.twoFactor.enterCode'));
      return;
    }
    setLoading2FA(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/auth/2fa/verify/sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code: smsCode })
      });
      const data = await response.json();
      if (data.success) {
        if (!backupCodes.length) {
          setBackupCodes(data.backupCodes || []);
        }
        setSmsVerified(true);
        setSmsCode('');
        setSmsCodeSent(false);
        await fetch2FAStatus();
        showNotification(t('admin.twoFactor.smsVerified'));
        
        // Don't auto-advance, let user click "Next" button
      } else {
        showNotification(data.message || t('admin.twoFactor.invalidCode'));
      }
    } catch (error: any) {
      showNotification(error.message || t('admin.twoFactor.verifyFailed'));
    } finally {
      setLoading2FA(false);
    }
  };

  const handleDisable2FA = async () => {
    setShowDisable2FADialog(true);
  };

  const confirmDisable2FA = async () => {
    setShowDisable2FADialog(false);
    setLoading2FA(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/auth/2fa/disable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        const hadAuthenticator = twoFAStatus?.hasAuthenticator || false;
        setQrCode(null);
        setManualEntryKey(null);
        setBackupCodes([]);
        setShowBackupCodes(false);
        await fetch2FAStatus();
        // Show appropriate success message based on whether they had authenticator app
        if (hadAuthenticator) {
          showNotification(t('admin.twoFactor.disabledSuccess'));
        } else {
          showNotification(t('admin.twoFactor.disabled'));
        }
      } else {
        showNotification(data.message || t('admin.twoFactor.disableFailed'));
      }
    } catch (error: any) {
      showNotification(error.message || t('admin.twoFactor.disableFailed'));
    } finally {
      setLoading2FA(false);
    }
  };

  const handleSaveAddress = async () => {
    console.log('🟢 [PROFILE] handleSaveAddress called');
    console.log('🟢 [PROFILE] user._id:', user?._id);
    console.log('🟢 [PROFILE] addressDetails:', addressDetails);
    
    if (!user?._id) {
      console.log('❌ [PROFILE] No user ID, aborting');
      setEditingAddress(false);
      return;
    }
    
    if (!addressDetails || !addressDetails.formatted) {
      console.log('❌ [PROFILE] No valid address selected');
      showNotification(t('admin.pleaseSelectValidAddress') || 'Please select a valid address');
      return;
    }
    
    setSavingAddress(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const url = `${apiUrl}/api/users/${user._id}`;
      
      console.log('🟢 [PROFILE] Sending PUT request to:', url);
      console.log('🟢 [PROFILE] Request body:', JSON.stringify({ address: addressDetails }, null, 2));
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ address: addressDetails })
      });

      console.log('🟢 [PROFILE] Response status:', response.status);
      const data = await response.json();
      console.log('🟢 [PROFILE] Response data:', JSON.stringify(data, null, 2));
      
      if (data.success) {
        console.log('✅ [PROFILE] Address saved successfully');
        // Log user data without profileImage to avoid cluttering console
        const userDataWithoutImage = data.data ? { ...data.data, profileImage: '[REDACTED]' } : null;
        console.log('✅ [PROFILE] Saved user data:', JSON.stringify(userDataWithoutImage, null, 2));
        console.log('✅ [PROFILE] Saved address in response:', JSON.stringify(data.data?.address, null, 2));
        
        if (refreshUser) {
          console.log('🔄 [PROFILE] Calling refreshUser...');
          await refreshUser();
          console.log('✅ [PROFILE] refreshUser completed');
        }
        setEditingAddress(false);
        showNotification(t('admin.addressSaved'));
      } else {
        console.log('❌ [PROFILE] Save failed:', data.message);
        throw new Error(data.message || 'Failed to update address');
      }
    } catch (error: any) {
      console.error('❌ [PROFILE] Error saving address:', error);
      const errorMessage = error?.message || 'Failed to update address';
      showNotification(errorMessage);
    } finally {
      setSavingAddress(false);
    }
  };

  const handleSavePhone = async () => {
    if (!user?._id) {
      setEditingPhone(false);
      return;
    }

    setSavingPhone(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      const response = await fetch(`${apiUrl}/api/users/${user._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ smsPhoneNumber: phoneNumber })
      });

      const data = await response.json();
      if (data.success) {
        if (refreshUser) await refreshUser();
        setEditingPhone(false);
        showNotification(t('admin.phoneNumberSaved'));
        // Refresh 2FA status in case phone was used for SMS 2FA
        await fetch2FAStatus();
      } else {
        throw new Error(data.message || 'Failed to update phone number');
      }
    } catch (error: any) {
      console.error('Failed to update phone number:', error);
      const errorMessage = error?.message || 'Failed to update phone number';
      showNotification(errorMessage);
      setPhoneNumber((user as any)?.smsPhoneNumber || '');
    } finally {
      setSavingPhone(false);
    }
  };

  if (!user) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500 dark:text-gray-400">{t('admin.loadingUserInformation')}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Profile Content */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 border border-gray-200 dark:border-gray-600">
        <div className="flex items-start space-x-6">
          {/* Profile Picture */}
          <div className="flex-shrink-0">
            <div className="relative group">
              <div className="w-24 h-24 rounded-full overflow-hidden bg-primary-500 flex items-center justify-center relative">
                {profileImage ? (
                  <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-12 h-12 text-white" />
                )}
                
                {/* Overlay on hover */}
                <div 
                  onClick={handleImageClick}
                  className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                >
                  <div className="text-white text-center">
                    {uploading ? (
                      <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Camera className="w-6 h-6" />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* User Info */}
          <div className="flex-1 space-y-3">
            {/* Name - Combined */}
            <div>
              {editingFirstName || editingLastName ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveFirstName()}
                    onBlur={handleSaveFirstName}
                    className="text-lg font-semibold px-2 py-1 border-0 border-b-2 border-transparent bg-transparent text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 focus:border-0 focus:border-b-2 focus:border-primary-500 transition-colors"
                    autoFocus
                    disabled={savingFirstName}
                    placeholder={t('auth.firstName')}
                  />
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveLastName()}
                    onBlur={handleSaveLastName}
                    className="text-lg font-semibold px-2 py-1 border-0 border-b-2 border-transparent bg-transparent text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 focus:border-0 focus:border-b-2 focus:border-primary-500 transition-colors"
                    disabled={savingLastName}
                    placeholder={t('auth.lastName')}
                  />
                </div>
              ) : (
                <div
                  onClick={() => {
                    setEditingFirstName(true);
                    setEditingLastName(true);
                  }}
                  className="group text-lg font-semibold text-gray-900 dark:text-gray-100 cursor-pointer hover:text-primary-500 transition-colors px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-600 inline-flex items-center space-x-2"
                >
                  <span>{user.firstName} {user.lastName}</span>
                  <Edit2 className="w-4 h-4 opacity-0 group-hover:opacity-50 transition-opacity" />
                </div>
              )}
            </div>
            
            {/* Email Row */}
            <div className="flex items-center space-x-2">
              <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
              {editingEmail ? (
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveEmail()}
                  onBlur={handleSaveEmail}
                  className="text-sm px-2 py-1 border-b-2 border-transparent bg-transparent text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 flex-1 transition-colors"
                  autoFocus
                  disabled={savingEmail}
                />
              ) : (
                <div
                  onClick={() => setEditingEmail(true)}
                  className="group text-sm text-gray-600 dark:text-gray-400 cursor-pointer hover:text-primary-500 transition-colors px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center space-x-2"
                >
                  <span>{user.email}</span>
                  <Edit2 className="w-3 h-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                </div>
              )}
            </div>

            {/* Role */}
            <div className="flex items-center space-x-2 text-xs">
              <span className="text-gray-500 dark:text-gray-400">{t('admin.role')}:</span>
              <span className="font-medium text-gray-900 dark:text-gray-100 px-2 py-1 bg-gray-200 dark:bg-gray-600 rounded">
                {user.role}
              </span>
            </div>

            {/* Address Row */}
            <div className="flex items-start space-x-2 pt-2">
              <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
              {editingAddress ? (
                <div className="flex-1 space-y-2">
                  <GoogleAddressAutocomplete
                    key="address-edit"
                    value={addressFormatted || (user as any)?.address?.formatted || ''}
                    onChange={setAddressFormatted}
                    onPlaceSelect={handleAddressSelect}
                    placeholder={t('admin.address')}
                    className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full transition-colors"
                    disabled={savingAddress}
                    autoFocus
                  />
                  <div className="flex space-x-2">
                    <button
                      onClick={handleSaveAddress}
                      disabled={savingAddress}
                      className="text-xs px-2 py-1 bg-success-500 hover:bg-success-600 text-white rounded disabled:opacity-50"
                    >
                      {savingAddress ? t('common.saving') : t('common.save')}
                    </button>
                    <button
                      onClick={() => {
                        setEditingAddress(false);
                        const userAddress = (user as any)?.address;
                        if (userAddress) {
                          setAddressDetails({
                            formatted: userAddress.formatted || [userAddress.street, userAddress.city, userAddress.state, userAddress.postalCode, userAddress.country].filter(Boolean).join(', '),
                            streetNumber: userAddress.streetNumber,
                            streetName: userAddress.streetName,
                            street: userAddress.street,
                            city: userAddress.city,
                            state: userAddress.state,
                            postalCode: userAddress.postalCode,
                            country: userAddress.country,
                            lat: userAddress.lat,
                            lng: userAddress.lng
                          });
                          setAddressFormatted(userAddress.formatted || [userAddress.street, userAddress.city, userAddress.state, userAddress.postalCode, userAddress.country].filter(Boolean).join(', '));
                        } else {
                          setAddressDetails(null);
                          setAddressFormatted('');
                        }
                      }}
                      className="text-xs px-2 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => {
                    // CRITICAL: Set addressFormatted BEFORE entering edit mode to prevent clearing
                    const userAddress = (user as any)?.address;
                    if (userAddress) {
                      const formatted = userAddress.formatted || [userAddress.street, userAddress.city, userAddress.state, userAddress.postalCode, userAddress.country].filter(Boolean).join(', ');
                      if (formatted && formatted !== addressFormatted) {
                        setAddressFormatted(formatted);
                        setAddressDetails({
                          formatted: formatted,
                          streetNumber: userAddress.streetNumber,
                          streetName: userAddress.streetName,
                          street: userAddress.street,
                          city: userAddress.city,
                          state: userAddress.state,
                          postalCode: userAddress.postalCode,
                          country: userAddress.country,
                          lat: userAddress.lat,
                          lng: userAddress.lng
                        });
                      }
                    }
                    setEditingAddress(true);
                  }}
                  className="group text-sm text-gray-600 dark:text-gray-400 cursor-pointer hover:text-primary-500 transition-colors px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-600 flex-1 flex items-center space-x-2"
                >
                  <span className="flex-1">
                    {addressFormatted || (user as any)?.address?.formatted || 
                      [(user as any)?.address?.street, (user as any)?.address?.city, (user as any)?.address?.state, (user as any)?.address?.postalCode, (user as any)?.address?.country]
                        .filter(Boolean).join(', ') || t('admin.noAddressSet')}
                  </span>
                  <Edit2 className="w-3 h-3 opacity-0 group-hover:opacity-50 transition-opacity flex-shrink-0" />
                </div>
              )}
            </div>

            {/* Phone Number Field - Under Address */}
            <div className="flex items-start space-x-2 pt-2">
              <Smartphone className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
              {editingPhone ? (
                <div className="flex-1 space-y-2">
                  <PhoneInput
                    value={phoneNumber || ''}
                    onChange={setPhoneNumber}
                    defaultCountry={account?.address?.country ? getCountryCode(account.address.country) : undefined}
                    placeholder="Phone number"
                    className="w-full"
                    disabled={savingPhone}
                  />
                  <div className="flex space-x-2">
                    <button
                      onClick={handleSavePhone}
                      disabled={savingPhone}
                      className="text-xs px-2 py-1 bg-success-500 hover:bg-success-600 text-white rounded disabled:opacity-50"
                    >
                      {savingPhone ? t('common.saving') : t('common.save')}
                    </button>
                    <button
                      onClick={() => {
                        setEditingPhone(false);
                        setPhoneNumber((user as any)?.smsPhoneNumber || '');
                      }}
                      className="text-xs px-2 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => setEditingPhone(true)}
                  className="group text-sm text-gray-600 dark:text-gray-400 cursor-pointer hover:text-primary-500 transition-colors px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-600 flex-1 flex items-center space-x-2"
                >
                  <span>{(user as any)?.smsPhoneNumber || t('admin.noPhoneSet')}</span>
                  <Edit2 className="w-3 h-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                </div>
              )}
            </div>

            {/* 2FA is now handled by 3PM Auth - section hidden */}
          </div>
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

            {/* Image Crop Uploader Modal */}
            {selectedFile && (
              <ImageCropUploader
                selectedImage={selectedFile}
                onImageCropped={handleImageCropped}
                onCancel={() => setSelectedFile(null)}
                aspectRatio={1}
                title={t('admin.cropProfilePicture')}
                maxSizeKB={5120}
              />
            )}

      {/* Email Change Modal */}
      {showEmailChangeModal && user && (
        <EmailChangeModal
          currentEmail={user.email}
          newEmail={pendingNewEmail}
          onClose={() => {
            setShowEmailChangeModal(false);
            setEmail(user.email);
          }}
          onSuccess={handleEmailChangeSuccess}
        />
      )}

      {/* Disable 2FA Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDisable2FADialog}
        onCancel={() => setShowDisable2FADialog(false)}
        onConfirm={confirmDisable2FA}
        title={t('admin.twoFactor.disable')}
        message={
          twoFAStatus?.hasAuthenticator ? (
            <div className="space-y-2">
              <p>{t('admin.twoFactor.confirmDisable')}</p>
              <div className="p-3 bg-yellow-50 dark:bg-gray-700 dark:border dark:border-warning-800/50 border border-yellow-200 dark:border-yellow-800 rounded-md">
                <p className="text-sm font-semibold text-warning-800 dark:text-warning-200 mb-1">
                  ⚠️ Important:
                </p>
                <p className="text-sm text-warning-700 dark:text-warning-300">
                  After disabling, you must manually remove this account from your authenticator app (Google Authenticator, Authy, etc.). The codes generated by your app will no longer work, but the entry will remain in your app until you delete it.
                </p>
              </div>
            </div>
          ) : (
            t('admin.twoFactor.confirmDisable')
          )
        }
        confirmLabel={t('admin.twoFactor.disable')}
        cancelLabel={t('common.cancel')}
        type="danger"
      />

      {/* 2FA Setup Modal */}
      {show2FASetupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('admin.twoFactor.setup')}
              </h3>
              {/* Hide close button when showing backup codes */}
              {!showBackupCodes && (
                <button
                  onClick={handleClose2FASetupModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Method Selection Step */}
              {setupStep === 'select' && (
                <>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('admin.twoFactor.selectMethods')}
                  </p>

                  <div className="space-y-3">
                    {/* Authenticator App Option */}
                    <button
                      onClick={() => handleToggleMethod('authenticator')}
                      className={`w-full p-4 border-2 rounded-lg transition-colors text-left ${
                        selected2FAMethods.authenticator
                          ? 'border-accent-500 bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50'
                          : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          selected2FAMethods.authenticator
                            ? 'border-accent-500 bg-accent-500'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          {selected2FAMethods.authenticator && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <Key className={`w-5 h-5 ${selected2FAMethods.authenticator ? 'text-accent-600 dark:text-accent-400' : 'text-gray-600 dark:text-gray-400'}`} />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 dark:text-gray-100">
                            {t('admin.twoFactor.authenticatorApp')}
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {t('admin.twoFactor.authenticatorDescription')}
                          </p>
                        </div>
                      </div>
                    </button>

                    {/* SMS Option */}
                    <button
                      onClick={() => handleToggleMethod('sms')}
                      className={`w-full p-4 border-2 rounded-lg transition-colors text-left ${
                        selected2FAMethods.sms
                          ? 'border-accent-500 bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50'
                          : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          selected2FAMethods.sms
                            ? 'border-accent-500 bg-accent-500'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          {selected2FAMethods.sms && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <Smartphone className={`w-5 h-5 ${selected2FAMethods.sms ? 'text-accent-600 dark:text-accent-400' : 'text-gray-600 dark:text-gray-400'}`} />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 dark:text-gray-100">
                            {t('admin.twoFactor.sms')}
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {t('admin.twoFactor.smsDescription')}
                          </p>
                        </div>
                      </div>
                    </button>
                  </div>

                  {/* Next Button */}
                  <button
                    onClick={handleNextFromSelection}
                    disabled={loading2FA || (!selected2FAMethods.authenticator && !selected2FAMethods.sms)}
                    className="w-full px-4 py-2 bg-accent-500 hover:bg-accent-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('common.next')}
                  </button>
                </>
              )}

              {/* Progress Indicator - Show when both methods selected */}
              {setupOrder.length > 1 && setupStep !== 'select' && (
                <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                    {t('admin.twoFactor.setupProgress') || 'Setup Progress'}
                  </p>
                  <div className="flex items-center gap-2">
                    {setupOrder.map((method, index) => {
                      const isActive = setupStep === method;
                      const isCompleted = (method === 'authenticator' && authenticatorVerified) || 
                                         (method === 'sms' && smsVerified);
                      const isPast = setupOrder.indexOf(setupStep) > index;
                      
                      return (
                        <div key={method} className="flex items-center flex-1">
                          <div className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-semibold ${
                            isCompleted
                              ? 'bg-success-500 text-white'
                              : isActive
                              ? 'bg-accent-500 text-white'
                              : isPast
                              ? 'bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                          }`}>
                            {isCompleted ? '✓' : index + 1}
                          </div>
                          <span className={`ml-2 text-xs ${
                            isActive
                              ? 'font-semibold text-gray-900 dark:text-gray-100'
                              : 'text-gray-600 dark:text-gray-400'
                          }`}>
                            {method === 'authenticator' ? t('admin.twoFactor.authenticatorApp') : t('admin.twoFactor.sms')}
                          </span>
                          {index < setupOrder.length - 1 && (
                            <div className={`flex-1 h-0.5 mx-2 ${
                              isPast || isCompleted
                                ? 'bg-success-500'
                                : 'bg-gray-300 dark:bg-gray-600'
                            }`} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Authenticator Setup */}
              {setupStep === 'authenticator' && selected2FAMethods.authenticator && (
                <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Key className="w-5 h-5" />
                      <span className="font-medium">{t('admin.twoFactor.authenticatorApp')}</span>
                      {authenticatorVerified && (
                        <span className="text-success-600 dark:text-success-400 text-sm">✓ {t('common.verified')}</span>
                      )}
                    </div>
                  </div>

                  {qrCode ? (
                    <div className="space-y-4">
                      <div className="text-center">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                          {t('admin.twoFactor.scanQR')}
                        </p>
                        <img src={qrCode} alt="QR Code" className="mx-auto w-48 h-48 border border-gray-300 dark:border-gray-600 rounded" />
                        {manualEntryKey && (
                          <div className="mt-4">
                            <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                              {t('admin.twoFactor.manualEntry')}:
                            </p>
                            <code className="text-xs bg-gray-200 dark:bg-gray-800 px-2 py-1 rounded font-mono">
                              {manualEntryKey}
                            </code>
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('admin.twoFactor.enterCode')}
                        </label>
                        <CodeInput
                          value={twoFACode}
                          onChange={setTwoFACode}
                        />
                        <button
                          onClick={handleVerifyAuthenticator}
                          disabled={loading2FA || twoFACode.length !== 6}
                          className="mt-4 w-full px-4 py-2 bg-success-500 hover:bg-success-600 text-white rounded-lg disabled:opacity-50"
                        >
                          {loading2FA ? t('common.saving') : t('admin.twoFactor.verify')}
                        </button>
                      </div>
                    </div>
                  ) : !authenticatorVerified && (
                    <button
                      onClick={handleSetupAuthenticator}
                      disabled={loading2FA}
                      className="w-full px-4 py-2 bg-accent-500 hover:bg-accent-600 text-white rounded-lg disabled:opacity-50"
                    >
                      {t('admin.twoFactor.setupAuthenticator')}
                    </button>
                  )}
                  
                  {/* Next Button - Show if verified and there's a next method */}
                  {authenticatorVerified && setupOrder.length > 1 && (
                    <button
                      onClick={() => {
                        const nextMethodIndex = setupOrder.indexOf('authenticator') + 1;
                        if (nextMethodIndex < setupOrder.length) {
                          const nextMethod = setupOrder[nextMethodIndex];
                          setSetupStep(nextMethod);
                          if (nextMethod === 'sms') {
                            // Reset SMS state for fresh setup
                            setSmsPhoneNumber('');
                            setSmsCode('');
                          } else if (nextMethod === 'authenticator') {
                            handleSetupAuthenticator();
                          }
                        }
                      }}
                      className="w-full mt-4 px-4 py-2 bg-accent-500 hover:bg-accent-600 text-white rounded-lg"
                    >
                      {t('common.next')}
                    </button>
                  )}
                </div>
              )}

              {/* SMS Setup */}
              {setupStep === 'sms' && selected2FAMethods.sms && (
                <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Smartphone className="w-5 h-5" />
                      <span className="font-medium">{t('admin.twoFactor.sms')}</span>
                      {smsVerified && (
                        <span className="text-success-600 dark:text-success-400 text-sm">✓ {t('common.verified')}</span>
                      )}
                    </div>
                  </div>

                  {!smsVerified && (
                    <>
                      {!smsCodeSent ? (
                        <div className="space-y-2">
                          <PhoneInput
                            value={smsPhoneNumber || ''}
                            onChange={(value) => {
                              // Store the phone number as user types
                              setSmsPhoneNumber(value);
                            }}
                            defaultCountry={account?.address?.country ? getCountryCode(account.address.country) : undefined}
                            placeholder="Phone number"
                            className="w-full"
                            disabled={loading2FA}
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleSetupSMS();
                            }}
                            disabled={loading2FA || !smsPhoneNumber || smsPhoneNumber.replace(/\D/g, '').length <= 4}
                            className="w-full px-4 py-2 bg-accent-500 hover:bg-accent-600 text-white rounded-lg disabled:opacity-50"
                          >
                            {t('admin.twoFactor.sendCode')}
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {t('admin.twoFactor.codeSentTo')} {smsPhoneNumber}
                          </p>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              {t('admin.twoFactor.enterCode')}
                            </label>
                            <CodeInput
                              value={smsCode}
                              onChange={setSmsCode}
                            />
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={handleVerifySMS}
                              disabled={loading2FA || smsCode.length !== 6}
                              className="flex-1 px-4 py-2 bg-success-500 hover:bg-success-600 text-white rounded-lg disabled:opacity-50"
                            >
                              {loading2FA ? t('common.saving') : t('admin.twoFactor.verify')}
                            </button>
                            <button
                              onClick={() => {
                                setSmsCode('');
                                handleSetupSMS();
                              }}
                              disabled={loading2FA}
                              className="px-4 py-2 bg-accent-500 hover:bg-accent-600 text-white rounded-lg disabled:opacity-50"
                            >
                              {t('common.resend')}
                            </button>
                            <button
                              onClick={() => {
                                setSmsPhoneNumber('');
                                setSmsCode('');
                                setSmsCodeSent(false);
                              }}
                              className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg"
                            >
                              {t('common.cancel')}
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Next Button - Show if verified and there's a next method */}
                  {smsVerified && setupOrder.length > 1 && (
                    <button
                      onClick={() => {
                        const nextMethodIndex = setupOrder.indexOf('sms') + 1;
                        if (nextMethodIndex < setupOrder.length) {
                          const nextMethod = setupOrder[nextMethodIndex];
                          setSetupStep(nextMethod);
                          if (nextMethod === 'authenticator') {
                            handleSetupAuthenticator();
                          } else if (nextMethod === 'sms') {
                            // Reset SMS state for fresh setup
                            setSmsPhoneNumber('');
                            setSmsCode('');
                          }
                        }
                      }}
                      className="w-full mt-4 px-4 py-2 bg-accent-500 hover:bg-accent-600 text-white rounded-lg"
                    >
                      {t('common.next')}
                    </button>
                  )}
                </div>
              )}

              {/* Backup Codes */}
              {showBackupCodes && backupCodes.length > 0 && (
                <div className="p-4 bg-yellow-50 dark:bg-gray-700 dark:border dark:border-warning-800/50 rounded-lg">
                  <p className="text-sm font-medium text-warning-800 dark:text-warning-300 mb-2">
                    {t('admin.twoFactor.backupCodes')}
                  </p>
                  <p className="text-xs text-warning-700 dark:text-warning-400 mb-3">
                    {t('admin.twoFactor.saveBackupCodes')}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {backupCodes.map((code, index) => (
                      <code key={index} className="text-xs bg-white dark:bg-gray-800 px-2 py-1 rounded font-mono text-center">
                        {code}
                      </code>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-between gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              {setupStep !== 'select' && !showBackupCodes && (
                <button
                  onClick={() => {
                    // Go back to selection
                    setSetupStep('select');
                    setQrCode(null);
                    setManualEntryKey(null);
                    setTwoFACode('');
                    setSmsPhoneNumber('');
                    setSmsCode('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  {t('common.back')}
                </button>
              )}
              <div className="flex gap-3 ml-auto">
                {showBackupCodes ? (
                  <button
                    onClick={() => {
                      setShowBackupCodes(false);
                      // Check if there are more methods to set up
                      const currentMethodIndex = setupOrder.findIndex(m => 
                        (m === 'authenticator' && !authenticatorVerified) || 
                        (m === 'sms' && !smsVerified)
                      );
                      if (currentMethodIndex === -1 || currentMethodIndex >= setupOrder.length - 1) {
                        // All methods completed - close the modal
                        handleClose2FASetupModal();
                      } else {
                        // Move to next method
                        const nextMethod = setupOrder[currentMethodIndex + 1];
                        setSetupStep(nextMethod);
                        if (nextMethod === 'authenticator' && !qrCode) {
                          handleSetupAuthenticator();
                        }
                      }
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-accent-500 hover:bg-accent-600 rounded-lg transition-colors"
                  >
                    {t('common.done')}
                  </button>
                ) : (
                  <button
                    onClick={handleClose2FASetupModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Preferences Section */}
      <div className="mt-6">
        <EmailPreferences />
      </div>

      {/* Save Notification */}
      {showSaveNotification && (
        <div className={`fixed bottom-4 right-4 ${saveMessage.toLowerCase().includes('failed') || saveMessage.toLowerCase().includes('invalid') || saveMessage.toLowerCase().includes('error') ? 'bg-error-500' : 'bg-success-500'} text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 animate-fade-in z-50`}>
          {saveMessage.toLowerCase().includes('failed') || saveMessage.toLowerCase().includes('invalid') || saveMessage.toLowerCase().includes('error') ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
          <span>{saveMessage}</span>
        </div>
      )}
    </div>
  );
};

